import * as THREE from 'three';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js';

let camera, scene, renderer, controls;
let moveForward = false;
let moveBackward = false;
let moveLeft = false;
let moveRight = false;
let canJump = false;
let isSprinting = false;

let velocity = new THREE.Vector3();
let direction = new THREE.Vector3();
let zombies = [];
let trees = [];

let stamina = 100;
let distance = 0;
const ZOMBIE_COUNT = 20;
const TREE_COUNT = 300;
const TREE_COLLISION_DISTANCE = 1.2;
const TREE_PUSH_FORCE = 0.8;
const MOVEMENT_SPEED = 30;
const SPRINT_MULTIPLIER = 1.4;
const ZOMBIE_SPEED = 0.15;
const ZOMBIE_DETECTION_RANGE = 30;
const ZOMBIE_CATCH_DISTANCE = 2;
const FOG_DENSITY = 0.015;
const CLEANUP_DISTANCE = 100;
const MIN_SPAWN_DISTANCE = 30;
const MAX_SPAWN_DISTANCE = 100;
const MIN_TREE_SPACING = 8;
const DAY_NIGHT_CYCLE_DURATION = 120;
let gameTime = 0;
let isGameOver = false;
let isNightTime = false;
const MINIMAP_SIZE = 150;
let backgroundMusic;
let sunLight, moonLight, ambientLight;
let sunBeams = [];
let clouds = [];
const CLOUD_COUNT = 5;
const VISIBLE_DISTANCE = 150;
const BEAM_COUNT = 3;
const BUTTERFLY_COUNT = 15;
const FLOWER_COUNT = 100;
let butterflies = [];

// Shared geometries and materials for optimization
const sharedTrunkGeometry = new THREE.CylinderGeometry(0.3, 0.4, 15, 6);
const sharedTrunkMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x4d2926,
    roughness: 0.9,
    metalness: 0.1
});
const sharedFoliageGeometries = [
    new THREE.ConeGeometry(1.2, 4, 6),
    new THREE.ConeGeometry(0.9, 4, 6),
    new THREE.ConeGeometry(0.6, 4, 6)
];
const sharedFoliageMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x0d3302,
    roughness: 0.8,
    metalness: 0.1,
    transparent: true,
    opacity: 0.9
});

// Create shared butterfly materials
const butterflyWingMaterial = new THREE.MeshStandardMaterial({
    color: 0xffaa00,
    roughness: 0.5,
    metalness: 0.1,
    side: THREE.DoubleSide
});

const butterflyBodyMaterial = new THREE.MeshStandardMaterial({
    color: 0x000000,
    roughness: 0.8,
    metalness: 0.1
});

// Create shared flower materials
const flowerPetalMaterial = new THREE.MeshStandardMaterial({
    color: 0xff69b4,
    roughness: 0.8,
    metalness: 0.1
});

const flowerCenterMaterial = new THREE.MeshStandardMaterial({
    color: 0xffff00,
    roughness: 0.8,
    metalness: 0.1
});

// Enhance tree materials
sharedTrunkMaterial.color.setHex(0x3d1f1d);
sharedFoliageMaterial.color.setHex(0x145214);

// Reduce update frequencies and optimize constants
const SPATIAL_GRID_UPDATE_INTERVAL = 30; // Update grid every 30 frames
const ZOMBIE_UPDATE_INTERVAL = 2; // Update zombies every 2 frames
const CLEANUP_INTERVAL = 60; // Cleanup every 60 frames
let frameCount = 0;

// Add frustum for visibility checks
const frustum = new THREE.Frustum();
const projScreenMatrix = new THREE.Matrix4();

function isInView(position) {
    projScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    frustum.setFromProjectionMatrix(projScreenMatrix);
    return frustum.containsPoint(position);
}

let dayMusic, nightMusic;
const INITIAL_DAY_LENGTH = 40; // Initial day length
const INITIAL_NIGHT_LENGTH = 100; // Shorter first night
const NIGHT_LENGTH_INCREASE = 30; // Each night gets longer by this amount
let currentDayLength = INITIAL_DAY_LENGTH;
let currentNightLength = INITIAL_NIGHT_LENGTH;

let nightCount = 0; // Track number of nights survived
const ZOMBIE_BASE_COUNT = 20; // Starting number of zombies
const ZOMBIE_COUNT_INCREASE = 5; // Additional zombies per night
const ZOMBIE_BASE_SPEED = 0.15; // Starting zombie speed
const ZOMBIE_SPEED_INCREASE = 0.03; // Speed increase per night
let currentZombieCount = ZOMBIE_BASE_COUNT;
let currentZombieSpeed = ZOMBIE_BASE_SPEED;

// Add camera bob parameters
let bobTimer = 0;
const BOB_SPEED = 2.5;
const BOB_AMOUNT = 0.08;

init();
animate();

function init() {
    scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x87ceeb, FOG_DENSITY * 0.3);

    // Add sky
    const skyGeometry = new THREE.SphereGeometry(500, 16, 16);
    skyGeometry.scale(-1, 1, 1);
    const skyMaterial = new THREE.MeshBasicMaterial({
        color: 0x87ceeb,
        side: THREE.BackSide,
        fog: false,
        depthWrite: false
    });
    const sky = new THREE.Mesh(skyGeometry, skyMaterial);
    scene.add(sky);

    // Add clouds
    createClouds();

    camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 1, 1000);
    camera.position.y = 1.7;

    // Enhanced lighting system - start with daytime lighting
    ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    scene.add(ambientLight);

    // Sun light (day)
    sunLight = new THREE.DirectionalLight(0xffffaa, 1);
    sunLight.position.set(100, 100, 100);
    sunLight.castShadow = true;
    sunLight.visible = true;
    scene.add(sunLight);

    // Add volumetric sun beams
    createSunBeams();

    // Moon light (night)
    moonLight = new THREE.DirectionalLight(0x6666ff, 0.5);
    moonLight.position.set(100, 100, 100);
    moonLight.castShadow = true;
    moonLight.visible = false;
    scene.add(moonLight);

    // Add point lights for atmosphere (only visible at night)
    const torch1 = new THREE.PointLight(0xff6600, 1, 50);
    torch1.position.set(-50, 5, -50);
    torch1.visible = false;
    scene.add(torch1);

    const torch2 = new THREE.PointLight(0xff6600, 1, 50);
    torch2.position.set(50, 5, 50);
    torch2.visible = false;
    scene.add(torch2);

    // Enhanced ground with texture - start with daytime color
    const groundGeometry = new THREE.PlaneGeometry(2000, 2000, 100, 100);
    const groundMaterial = new THREE.MeshStandardMaterial({ 
        color: 0x2d5a27, // Bright daytime grass color
        roughness: 0.9,
        metalness: 0.1,
        wireframe: false
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);

    // Add day/night indicator to UI - start with day message
    const timeIndicator = document.createElement('div');
    timeIndicator.id = 'time-indicator';
    timeIndicator.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        color: #ffff00;
        font-family: 'Arial', sans-serif;
        font-size: 24px;
        text-shadow: 2px 2px 2px rgba(0,0,0,0.5);
        background: rgba(0,0,0,0.3);
        padding: 10px;
        border-radius: 10px;
        backdrop-filter: blur(5px);
    `;
    timeIndicator.innerHTML = '☀️';
    document.body.appendChild(timeIndicator);

    // Add trees
    for (let i = 0; i < TREE_COUNT; i++) {
        addTree();
    }

    // Add flowers
    for (let i = 0; i < FLOWER_COUNT; i++) {
        const flower = createFlower();
        const angle = Math.random() * Math.PI * 2;
        const radius = 5 + Math.random() * 95;
        flower.position.set(
            Math.cos(angle) * radius,
            0,
            Math.sin(angle) * radius
        );
        flower.rotation.y = Math.random() * Math.PI * 2;
        scene.add(flower);
    }

    // Add butterflies
    for (let i = 0; i < BUTTERFLY_COUNT; i++) {
        const butterfly = createButterfly();
        scene.add(butterfly);
        butterflies.push(butterfly);
    }

    // Don't add any zombies at start - they'll spawn when night comes

    renderer = new THREE.WebGLRenderer({ 
        antialias: true,
        logarithmicDepthBuffer: true,
        powerPreference: "high-performance"
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x87ceeb);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    document.body.appendChild(renderer.domElement);

    // Controls
    controls = new PointerLockControls(camera, document.body);

    const startScreen = document.getElementById('start-screen');
    
    controls.addEventListener('lock', function() {
        startScreen.style.display = 'none';
        if (dayMusic && dayMusic.paused && !isNightTime) {
            dayMusic.play();
            document.getElementById('toggle-music').textContent = '🔊 Music';
        } else if (nightMusic && nightMusic.paused && isNightTime) {
            nightMusic.play();
            document.getElementById('toggle-music').textContent = '🔊 Music';
        }
    });

    controls.addEventListener('unlock', function() {
        if (!isGameOver) {
            startScreen.style.display = 'block';
        }
    });

    document.addEventListener('click', function () {
        controls.lock();
    });

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);

    window.addEventListener('resize', onWindowResize, false);

    // Add survival time display to UI
    const timeDisplay = document.createElement('div');
    timeDisplay.id = 'survival-time';
    document.getElementById('ui-overlay').appendChild(timeDisplay);

    setupMinimap();
    setupAudio();
    if (!isNightTime) {
        dayMusic.play(); // Start with day music since we start in daytime
    }
}

function setupMinimap() {
    const minimapContainer = document.createElement('div');
    minimapContainer.id = 'minimap';
    minimapContainer.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        width: ${MINIMAP_SIZE}px;
        height: ${MINIMAP_SIZE}px;
        background: rgba(0,0,0,0.5);
        border: 2px solid #fff;
        border-radius: 50%;
        overflow: hidden;
    `;
    
    const minimapCanvas = document.createElement('canvas');
    minimapCanvas.id = 'minimap-canvas';
    minimapCanvas.width = MINIMAP_SIZE;
    minimapCanvas.height = MINIMAP_SIZE;
    minimapContainer.appendChild(minimapCanvas);
    document.body.appendChild(minimapContainer);
}

function updateMinimap() {
    const canvas = document.getElementById('minimap-canvas');
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, MINIMAP_SIZE, MINIMAP_SIZE);

    // Scale factor for minimap (adjust as needed)
    const scale = MINIMAP_SIZE / 200;
    const centerX = MINIMAP_SIZE / 2;
    const centerY = MINIMAP_SIZE / 2;

    // Draw player (white dot in center)
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(centerX, centerY, 3, 0, Math.PI * 2);
    ctx.fill();

    // Draw zombies (red dots)
    ctx.fillStyle = '#ff0000';
    zombies.forEach(zombie => {
        const relX = (zombie.position.x - camera.position.x) * scale + centerX;
        const relZ = (zombie.position.z - camera.position.z) * scale + centerY;
        
        // Only draw if within minimap bounds
        if (relX >= 0 && relX <= MINIMAP_SIZE && relZ >= 0 && relZ <= MINIMAP_SIZE) {
            ctx.beginPath();
            ctx.arc(relX, relZ, 2, 0, Math.PI * 2);
            ctx.fill();
        }
    });
}

function addTree() {
    const tree = new THREE.Group();

    // Make trunk taller and more majestic
    const trunkHeight = 15 + Math.random() * 10;
    const trunk = new THREE.Mesh(
        new THREE.CylinderGeometry(0.4, 0.6, trunkHeight, 6),
        sharedTrunkMaterial
    );
    tree.add(trunk);

    // Add more layers of foliage for a fuller look
    const foliageLayers = 4 + Math.floor(Math.random() * 3);
    for (let i = 0; i < foliageLayers; i++) {
        const size = 2.5 - (i * 0.3);
        const foliage = new THREE.Mesh(
            new THREE.ConeGeometry(size, 4, 6),
            sharedFoliageMaterial
        );
        foliage.position.y = (trunkHeight * 0.5) + (i * 2.5);
        tree.add(foliage);
    }

    // Store materials for day/night transitions
    tree.userData = {
        trunkMaterial: sharedTrunkMaterial,
        foliageMaterials: tree.children.slice(1).map(f => f.material)
    };

    // Try to find a valid position for the tree
    let attempts = 0;
    let validPosition = false;
    
    while (!validPosition && attempts < 10) {
        const angle = Math.random() * Math.PI * 2;
        const distance = MIN_SPAWN_DISTANCE + Math.random() * (MAX_SPAWN_DISTANCE - MIN_SPAWN_DISTANCE);
        const offsetX = (Math.random() - 0.5) * 40;
        const offsetZ = (Math.random() - 0.5) * 40;
        
        tree.position.x = camera.position.x + Math.cos(angle) * distance + offsetX;
        tree.position.z = camera.position.z + Math.sin(angle) * distance + offsetZ;

        validPosition = checkTreePlacement(tree.position);
        attempts++;
    }

    if (validPosition) {
        // Add some random rotation for variety
        tree.rotation.y = Math.random() * Math.PI * 2;
        // Random scale for more variety
        const scale = 0.8 + Math.random() * 0.4;
        tree.scale.set(scale, scale, scale);
        scene.add(tree);
        trees.push(tree);
    }
}

function checkTreePlacement(position) {
    for (const tree of trees) {
        const dx = position.x - tree.position.x;
        const dz = position.z - tree.position.z;
        const distance = Math.sqrt(dx * dx + dz * dz);
        if (distance < MIN_TREE_SPACING) {
            return false;
        }
    }
    return true;
}

function addZombie() {
    // Create zombie body
    const bodyGeometry = new THREE.BoxGeometry(0.8, 1.6, 0.5);
    const zombieMaterial = new THREE.MeshStandardMaterial({ 
        color: 0x2a4d2a,
        roughness: 0.9,
        metalness: 0.1
    });
    const zombie = new THREE.Group();
    const body = new THREE.Mesh(bodyGeometry, zombieMaterial);
    body.position.y = 0.8;
    zombie.add(body);

    // Add head
    const headGeometry = new THREE.BoxGeometry(0.4, 0.4, 0.4);
    const head = new THREE.Mesh(headGeometry, zombieMaterial);
    head.position.y = 1.9;
    zombie.add(head);

    // Add glowing red eyes
    const eyeGeometry = new THREE.SphereGeometry(0.05, 8, 8);
    const eyeMaterial = new THREE.MeshStandardMaterial({
        color: 0xff0000,
        emissive: 0xff0000,
        emissiveIntensity: 2.0,
        transparent: true,
        opacity: 0.9
    });

    // Left eye
    const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    leftEye.position.set(0.1, 1.9, 0.2);
    zombie.add(leftEye);

    // Right eye
    const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    rightEye.position.set(-0.1, 1.9, 0.2);
    zombie.add(rightEye);

    // Add arms
    const armGeometry = new THREE.BoxGeometry(0.2, 0.8, 0.2);
    const leftArm = new THREE.Mesh(armGeometry, zombieMaterial);
    leftArm.position.set(0.5, 1.2, 0);
    leftArm.rotation.z = -0.2;
    zombie.add(leftArm);

    const rightArm = new THREE.Mesh(armGeometry, zombieMaterial);
    rightArm.position.set(-0.5, 1.2, 0);
    rightArm.rotation.z = 0.2;
    zombie.add(rightArm);

    // Store references to materials for day/night transitions
    zombie.userData = {
        bodyMaterial: zombieMaterial,
        eyeMaterial: eyeMaterial
    };

    // More random zombie positioning
    const angle = Math.random() * Math.PI * 2;
    const distance = MIN_SPAWN_DISTANCE + Math.random() * (MAX_SPAWN_DISTANCE - MIN_SPAWN_DISTANCE);
    const offsetX = (Math.random() - 0.5) * 30;
    const offsetZ = (Math.random() - 0.5) * 30;
    
    zombie.position.x = camera.position.x + Math.cos(angle) * distance + offsetX;
    zombie.position.z = camera.position.z + Math.sin(angle) * distance + offsetZ;

    // Only add zombie if it's not inside a tree
    if (checkCollisions(zombie.position)) {
        scene.add(zombie);
        zombies.push(zombie);
        return zombie;
    }
    return null;
}

function onKeyDown(event) {
    switch (event.code) {
        case 'ArrowUp':
        case 'KeyW':
            moveForward = true;
            break;
        case 'ArrowDown':
        case 'KeyS':
            moveBackward = true;
            break;
        case 'ArrowLeft':
        case 'KeyA':
            moveLeft = true;
            break;
        case 'ArrowRight':
        case 'KeyD':
            moveRight = true;
            break;
        case 'ShiftLeft':
            if (stamina > 0) isSprinting = true;
            break;
        case 'Space':
            // Emergency escape - teleport slightly upward and backward from current direction
            if (checkCollisions(camera.position).collision) {
                const lookingDirection = new THREE.Vector3();
                camera.getWorldDirection(lookingDirection);
                const escapePosition = new THREE.Vector3(
                    camera.position.x - lookingDirection.x * 3,
                    camera.position.y,
                    camera.position.z - lookingDirection.z * 3
                );
                if (!checkCollisions(escapePosition).collision) {
                    controls.getObject().position.copy(escapePosition);
                }
            }
            break;
    }
}

function onKeyUp(event) {
    switch (event.code) {
        case 'ArrowUp':
        case 'KeyW':
            moveForward = false;
            break;
        case 'ArrowDown':
        case 'KeyS':
            moveBackward = false;
            break;
        case 'ArrowLeft':
        case 'KeyA':
            moveLeft = false;
            break;
        case 'ArrowRight':
        case 'KeyD':
            moveRight = false;
            break;
        case 'ShiftLeft':
            isSprinting = false;
            break;
    }
}

function onWindowResize() {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
}

const GRID_SIZE = 50;
const spatialGrid = new Map();

function updateSpatialGrid() {
    spatialGrid.clear();
    trees.forEach(tree => {
        const gridX = Math.floor(tree.position.x / GRID_SIZE);
        const gridZ = Math.floor(tree.position.z / GRID_SIZE);
        const key = `${gridX},${gridZ}`;
        if (!spatialGrid.has(key)) {
            spatialGrid.set(key, []);
        }
        spatialGrid.get(key).push(tree);
    });
}

function getNearbyTrees(position, radius) {
    const gridX = Math.floor(position.x / GRID_SIZE);
    const gridZ = Math.floor(position.z / GRID_SIZE);
    const nearby = [];
    
    // Check only immediate neighboring cells
    for (let x = -1; x <= 1; x++) {
        for (let z = -1; z <= 1; z++) {
            const key = `${gridX + x},${gridZ + z}`;
            const cellTrees = spatialGrid.get(key);
            if (cellTrees) {
                // Only add trees within actual radius
                cellTrees.forEach(tree => {
                    const dx = position.x - tree.position.x;
                    const dz = position.z - tree.position.z;
                    const distSq = dx * dx + dz * dz;
                    if (distSq < radius * radius) {
                        nearby.push(tree);
                    }
                });
            }
        }
    }
    return nearby;
}

function checkCollisions(newPosition) {
    const nearbyTrees = getNearbyTrees(newPosition, TREE_COLLISION_DISTANCE * 2);
    let closestDistance = Infinity;
    let closestTree = null;

    for (const tree of nearbyTrees) {
        const dx = newPosition.x - tree.position.x;
        const dz = newPosition.z - tree.position.z;
        const distance = Math.sqrt(dx * dx + dz * dz);
        
        if (distance < closestDistance) {
            closestDistance = distance;
            closestTree = tree;
        }
        
        if (distance < TREE_COLLISION_DISTANCE + 0.8) {
            return { collision: true, tree, distance };
        }
    }
    return { collision: false, tree: closestTree, distance: closestDistance };
}

function handleCollision(newPosition, currentPosition) {
    const collisionResult = checkCollisions(newPosition);
    
    if (!collisionResult.collision) {
        return newPosition;
    }

    // Find all nearby trees
    const nearbyTrees = trees.filter(tree => {
        const dist = newPosition.distanceTo(tree.position);
        return dist < TREE_COLLISION_DISTANCE * 2;
    });

    if (nearbyTrees.length > 0) {
        // Calculate average push direction from all nearby trees
        const pushDirection = new THREE.Vector3();
        nearbyTrees.forEach(tree => {
            const dir = new THREE.Vector3();
            dir.subVectors(currentPosition, tree.position).normalize();
            const dist = newPosition.distanceTo(tree.position);
            const strength = 1 / Math.max(dist, 0.1);
            pushDirection.add(dir.multiplyScalar(strength));
        });
        pushDirection.normalize();

        // Create a slide vector parallel to the push direction
        const slideVector = new THREE.Vector3();
        slideVector.crossVectors(pushDirection, new THREE.Vector3(0, 1, 0));

        // Try sliding along the wall
        const slidePosition = new THREE.Vector3();
        slidePosition.copy(currentPosition).add(slideVector.multiplyScalar(0.5));

        // If sliding works, use that position
        if (!checkCollisions(slidePosition).collision) {
            return slidePosition;
        }

        // If sliding doesn't work, push away from trees
        const safePosition = new THREE.Vector3();
        safePosition.copy(currentPosition).add(pushDirection.multiplyScalar(0.5));
        
        return safePosition;
    }

    // Fallback to current position if all else fails
    return currentPosition;
}

function updateZombies() {
    if (isGameOver || !isNightTime) return;

    let nearbyZombies = 0;
    const playerPos = camera.position;
    
    zombies.forEach(zombie => {
        const dx = playerPos.x - zombie.position.x;
        const dz = playerPos.z - zombie.position.z;
        const distSq = dx * dx + dz * dz;
        
        if (distSq < ZOMBIE_DETECTION_RANGE * ZOMBIE_DETECTION_RANGE) {
            // Normalize direction without creating new vectors
            const dist = Math.sqrt(distSq);
            const dirX = dx / dist;
            const dirZ = dz / dist;
            
            // Check for nearby trees to avoid
            const nearbyTrees = getNearbyTrees(zombie.position, TREE_COLLISION_DISTANCE * 3);
            let avoidX = 0;
            let avoidZ = 0;

            // Calculate tree avoidance if there are nearby trees
            if (nearbyTrees.length > 0) {
                nearbyTrees.forEach(tree => {
                    const tdx = zombie.position.x - tree.position.x;
                    const tdz = zombie.position.z - tree.position.z;
                    const tDistSq = tdx * tdx + tdz * tdz;
                    const tDist = Math.sqrt(tDistSq);
                    const strength = 1 / Math.max(tDist, 0.1);
                    avoidX += (tdx / tDist) * strength;
                    avoidZ += (tdz / tDist) * strength;
                });
            }
            
            // Calculate final movement direction
            let finalDirX = dirX;
            let finalDirZ = dirZ;
            
            // Blend with avoidance if there are trees
            if (nearbyTrees.length > 0) {
                const blendFactor = 0.5;
                finalDirX += avoidX * blendFactor;
                finalDirZ += avoidZ * blendFactor;
                // Normalize the direction
                const normFactor = 1 / Math.sqrt(finalDirX * finalDirX + finalDirZ * finalDirZ);
                finalDirX *= normFactor;
                finalDirZ *= normFactor;
            }
            
            // Move zombie
            const newX = zombie.position.x + finalDirX * currentZombieSpeed;
            const newZ = zombie.position.z + finalDirZ * currentZombieSpeed;
            
            // Check for collisions before moving
            const newPos = { x: newX, z: newZ };
            if (!checkCollisions(newPos).collision) {
                zombie.position.x = newX;
                zombie.position.z = newZ;
            }
            
            // Make zombie face player
            zombie.rotation.y = Math.atan2(dx, dz);
        }
        
        if (distSq < ZOMBIE_CATCH_DISTANCE * ZOMBIE_CATCH_DISTANCE) {
            nearbyZombies++;
        }
    });
    
    document.getElementById('zombies').textContent = nearbyZombies;
    if (nearbyZombies >= 2) {
        gameOver();
    }
}

function updateStamina() {
    if (isSprinting) {
        stamina = Math.max(0, stamina - 0.5);
    } else {
        stamina = Math.min(100, stamina + 0.2);
    }
    document.getElementById('stamina-fill').style.width = stamina + '%';
}

function gameOver() {
    if (isGameOver) return;
    
    isGameOver = true;
    controls.unlock();
    const finalTime = Math.floor(gameTime);
    const minutes = Math.floor(finalTime / 60);
    const seconds = finalTime % 60;
    
    alert(`Game Over! You survived ${nightCount} nights!\nTotal survival time: ${minutes}m ${seconds}s`);
    location.reload();
}

function cleanupObjects() {
    // Clean up trees that are too far or not visible
    trees = trees.filter(tree => {
        const distance = tree.position.distanceTo(camera.position);
        if (distance > CLEANUP_DISTANCE || !isInView(tree.position)) {
            scene.remove(tree);
            return false;
        }
        // Hide trees that are too far but keep them in memory
        tree.visible = distance < VISIBLE_DISTANCE;
        return true;
    });

    // Clean up zombies that are too far
    zombies = zombies.filter(zombie => {
        const distance = zombie.position.distanceTo(camera.position);
        if (distance > CLEANUP_DISTANCE) {
            scene.remove(zombie);
            return false;
        }
        // Hide far zombies but keep them in memory
        zombie.visible = distance < VISIBLE_DISTANCE;
        return true;
    });

    // Add new trees if needed, with more variance in the count
    const targetTreeCount = TREE_COUNT - Math.floor(Math.random() * 100);
    while (trees.length < targetTreeCount) {
        addTree();
    }
}

function respawnZombies() {
    if (!isNightTime) return;

    // Use current night's zombie count
    while (zombies.length < currentZombieCount) {
        const zombie = addZombie();
        if (zombie) {
            // Add spawn-in effect
            zombie.scale.y = 0.1;
            zombie.position.y = -2;
            const fadeIn = setInterval(() => {
                if (zombie.scale.y < 1) {
                    zombie.scale.y += 0.1;
                    zombie.position.y += 0.2;
                } else {
                    clearInterval(fadeIn);
                }
            }, 50);
        }
    }
}

function setupAudio() {
    // Background music setup
    dayMusic = new Audio('day.mp3');
    dayMusic.loop = true;
    dayMusic.volume = 0.5;

    nightMusic = new Audio('night.mp3');
    nightMusic.loop = true;
    nightMusic.volume = 0.5;

    // Add music controls to UI
    const musicControls = document.createElement('div');
    musicControls.id = 'music-controls';
    musicControls.innerHTML = `
        <button id="toggle-music">🔇 Music</button>
        <input type="range" id="volume-slider" min="0" max="100" value="50">
    `;
    document.getElementById('ui-overlay').appendChild(musicControls);

    // Setup music toggle
    document.getElementById('toggle-music').addEventListener('click', toggleMusic);
    document.getElementById('volume-slider').addEventListener('input', adjustVolume);
}

function toggleMusic() {
    if (isNightTime) {
        if (nightMusic.paused) {
            nightMusic.play();
            document.getElementById('toggle-music').textContent = '🔊 Music';
        } else {
            nightMusic.pause();
            document.getElementById('toggle-music').textContent = '🔇 Music';
        }
    } else {
        if (dayMusic.paused) {
            dayMusic.play();
            document.getElementById('toggle-music').textContent = '🔊 Music';
        } else {
            dayMusic.pause();
            document.getElementById('toggle-music').textContent = '🔇 Music';
        }
    }
}

function adjustVolume(e) {
    const volume = e.target.value / 100;
    dayMusic.volume = volume;
    nightMusic.volume = volume;
}

function createSunBeams() {
    // Remove old sun beams
    sunBeams.forEach(beam => scene.remove(beam));
    sunBeams = [];

    // Create fewer beams
    const beamGeometry = new THREE.BoxGeometry(0.5, 50, 0.5);
    const beamMaterial = new THREE.MeshBasicMaterial({
        color: 0xffffdd,
        transparent: true,
        opacity: 0.2,
        blending: THREE.AdditiveBlending,
        depthWrite: false // Optimize transparency rendering
    });

    for (let i = 0; i < BEAM_COUNT; i++) {
        const beam = new THREE.Mesh(beamGeometry, beamMaterial.clone());
        beam.position.y = 25;
        beam.rotation.x = Math.random() * 0.2 - 0.1;
        beam.rotation.z = Math.random() * 0.2 - 0.1;
        beam.visible = false; // Start invisible
        sunBeams.push(beam);
        scene.add(beam);
    }
}

function updateSunBeams() {
    if (!isNightTime) {
        // Update sun beam positions to follow player
        sunBeams.forEach((beam, index) => {
            beam.position.x = camera.position.x + Math.cos(index * Math.PI * 0.4) * 20;
            beam.position.z = camera.position.z + Math.sin(index * Math.PI * 0.4) * 20;
            
            // Add subtle movement
            beam.rotation.x = Math.sin(gameTime * 0.5 + index) * 0.1;
            beam.rotation.z = Math.cos(gameTime * 0.5 + index) * 0.1;
            
            // Fade based on time of day
            const dayProgress = (gameTime % DAY_NIGHT_CYCLE_DURATION) / DAY_NIGHT_CYCLE_DURATION;
            const fadeEffect = Math.sin(dayProgress * Math.PI);
            beam.material.opacity = Math.max(0, fadeEffect * 0.2);
        });
    }
}

function createClouds() {
    clouds.forEach(cloud => scene.remove(cloud));
    clouds = [];

    // Create fewer, optimized clouds
    for (let i = 0; i < CLOUD_COUNT; i++) {
        const cloudGroup = new THREE.Group();
        const layerCount = 3 + Math.floor(Math.random() * 3); // Fewer layers
        
        // Reuse geometry and material for cloud parts
        const geometry = new THREE.SphereGeometry(8, 6, 6); // Reduced segments
        const material = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.3,
            depthWrite: false,
            roughness: 1,
            metalness: 0
        });
        
        for (let j = 0; j < layerCount; j++) {
            const cloudLayer = new THREE.Mesh(geometry, material);
            const size = 8 + Math.random() * 8; // Smaller size range
            cloudLayer.scale.set(size/8, size/8 * 0.3, size/8);
            cloudLayer.position.set(
                (Math.random() - 0.5) * size,
                (Math.random() - 0.5) * size * 0.3,
                (Math.random() - 0.5) * size
            );
            cloudGroup.add(cloudLayer);
        }
        
        cloudGroup.position.set(
            (Math.random() - 0.5) * 200,
            80 + Math.random() * 20,
            (Math.random() - 0.5) * 200
        );
        
        clouds.push(cloudGroup);
        scene.add(cloudGroup);
    }
}

function updateClouds() {
    const windSpeed = 0.2;
    clouds.forEach((cloud, index) => {
        // Move clouds along X axis
        cloud.position.x += windSpeed;
        
        // If cloud goes too far, wrap it around
        if (cloud.position.x > 250) {
            cloud.position.x = -250;
            cloud.position.z = (Math.random() - 0.5) * 300;
            cloud.position.y = 80 + Math.random() * 30;
        }

        // Add gentle floating motion
        cloud.position.y += Math.sin(gameTime * 0.5 + index) * 0.02;
        
        // Add slight rotation for more dynamic movement
        cloud.rotation.y = Math.sin(gameTime * 0.2 + index) * 0.02;
    });
}

function updateDayNightCycle() {
    const totalTime = gameTime % (currentDayLength + currentNightLength);
    const isNewNightTime = totalTime >= currentDayLength;
    
    // Find the sky
    const sky = scene.children.find(child => child.geometry && child.geometry.type === 'SphereGeometry');
    
    // Smoothly transition colors based on time of day
    const dayColor = new THREE.Color(0x87ceeb);
    const nightColor = new THREE.Color(0x000000);
    const currentColor = new THREE.Color();
    
    // Calculate transition factor with sharper night transition
    let transitionFactor;
    if (totalTime < currentDayLength * 0.25) { // Dawn
        transitionFactor = Math.max(0, 1 - (totalTime / (currentDayLength * 0.25)));
    } else if (totalTime < currentDayLength) { // Day
        transitionFactor = 0;
    } else if (totalTime < currentDayLength + (currentNightLength * 0.15)) { // Dusk - faster transition to darkness
        transitionFactor = Math.pow((totalTime - currentDayLength) / (currentNightLength * 0.15), 1.5); // Sharper curve
    } else { // Night
        transitionFactor = 1;
    }

    // Update scene state
    if (isNewNightTime !== isNightTime) {
        isNightTime = isNewNightTime;
        
        if (isNightTime) {
            // It's becoming night
            nightCount++;
            // Make nights progressively longer
            currentNightLength = INITIAL_NIGHT_LENGTH + (nightCount - 1) * NIGHT_LENGTH_INCREASE;
            
            // Update difficulty
            currentZombieCount = ZOMBIE_BASE_COUNT + (nightCount - 1) * ZOMBIE_COUNT_INCREASE;
            currentZombieSpeed = ZOMBIE_BASE_SPEED + (nightCount - 1) * ZOMBIE_SPEED_INCREASE;
            
            // Update UI to show night count
            document.getElementById('time-indicator').innerHTML = 
                `🌙`;
            document.getElementById('time-indicator').style.color = '#aaaaff';

            // Hide butterflies during night
            butterflies.forEach(butterfly => {
                const fadeOut = setInterval(() => {
                    if (butterfly.scale.y > 0.1) {
                        butterfly.scale.y -= 0.1;
                        butterfly.position.y -= 0.2;
                    } else {
                        clearInterval(fadeOut);
                        butterfly.visible = false;
                    }
                }, 50);
            });

            // Switch music with crossfade
            const fadeInterval = setInterval(() => {
                if (dayMusic.volume > 0.02) {
                    dayMusic.volume = Math.max(0, dayMusic.volume - 0.02);
                    nightMusic.volume = Math.min(0.5, nightMusic.volume + 0.02);
                } else {
                    dayMusic.pause();
                    clearInterval(fadeInterval);
                }
            }, 50);
            nightMusic.play();

            // Start spawning zombies at night
            respawnZombies();
        } else {
            // It's becoming day
            document.getElementById('time-indicator').innerHTML = 
                `☀️`;
            document.getElementById('time-indicator').style.color = '#ffff00';

            // Show butterflies during day
            butterflies.forEach(butterfly => {
                butterfly.visible = true;
                butterfly.scale.set(1, 1, 1);
                butterfly.position.y = butterfly.userData.originalY;
                
                // Randomize their positions around the player
                const angle = Math.random() * Math.PI * 2;
                const radius = 10 + Math.random() * 20;
                butterfly.position.set(
                    camera.position.x + Math.cos(angle) * radius,
                    butterfly.userData.originalY,
                    camera.position.z + Math.sin(angle) * radius
                );
            });

            // Switch music with crossfade
            const fadeInterval = setInterval(() => {
                if (nightMusic.volume > 0.02) {
                    nightMusic.volume = Math.max(0, nightMusic.volume - 0.02);
                    dayMusic.volume = Math.min(0.5, dayMusic.volume + 0.02);
                } else {
                    nightMusic.pause();
                    clearInterval(fadeInterval);
                }
            }, 50);
            dayMusic.play();

            // Remove all zombies during day
            zombies.forEach(zombie => {
                const fadeOut = setInterval(() => {
                    if (zombie.scale.y > 0.1) {
                        zombie.scale.y -= 0.1;
                        zombie.position.y -= 0.2;
                    } else {
                        clearInterval(fadeOut);
                        scene.remove(zombie);
                    }
                }, 50);
            });
            zombies = [];
        }
        
        // Update visibility of objects based on time of day
        sunBeams.forEach(beam => beam.visible = !isNightTime);
        clouds.forEach(cloud => {
            cloud.children.forEach(layer => {
                layer.material.opacity = isNightTime ? 0.15 : 0.3;
                layer.material.color.setHex(isNightTime ? 0x101014 : 0xffffff);
            });
        });

        // Update UI
        document.getElementById('time-indicator').innerHTML = isNightTime ? 
            '🌙' : 
            '☀️';
        document.getElementById('time-indicator').style.color = isNightTime ? 
            '#aaaaff' : '#ffff00';
    }
    
    // Update colors with more dramatic transition
    const transitionPower = isNightTime ? 2.0 : 0.5; // Sharper transition to darkness
    currentColor.copy(dayColor).lerp(nightColor, Math.pow(transitionFactor, transitionPower));
    if (sky) {
        sky.material.color.copy(currentColor);
    }
    
    // More dramatic exposure changes
    renderer.toneMappingExposure = isNightTime ? 
        0.3 * (1 - Math.min(1, nightCount * 0.2)) : // Gets darker each night
        1.0;
    
    // Update scene background and fog with increased darkness
    scene.background = currentColor;
    scene.fog.color.copy(currentColor);
    scene.fog.density = FOG_DENSITY * (0.3 + transitionFactor * (0.7 + Math.min(1, nightCount * 0.1))); // Fog gets thicker each night
}

function animate() {
    requestAnimationFrame(animate);

    if (controls.isLocked === true && !isGameOver) {
        const delta = 0.016;
        frameCount++;

        // Update butterflies
        if (!isNightTime) {
            butterflies.forEach(butterfly => {
                // Wing flapping
                butterfly.userData.wingAngle += butterfly.userData.flapSpeed;
                const wingRotation = Math.sin(butterfly.userData.wingAngle) * 0.5;
                butterfly.children[1].rotation.y = wingRotation; // Left wing
                butterfly.children[2].rotation.y = -wingRotation; // Right wing

                // Movement
                butterfly.userData.moveAngle += butterfly.userData.moveSpeed;
                butterfly.position.x += Math.sin(butterfly.userData.moveAngle) * 0.1;
                butterfly.position.z += Math.cos(butterfly.userData.moveAngle) * 0.1;
                butterfly.position.y = butterfly.userData.originalY + Math.sin(gameTime) * 0.5;

                // Rotate to face movement direction
                butterfly.rotation.y = butterfly.userData.moveAngle;

                // Keep butterflies near the player
                const distanceToPlayer = butterfly.position.distanceTo(camera.position);
                if (distanceToPlayer > 30) {
                    const angle = Math.random() * Math.PI * 2;
                    const radius = 10 + Math.random() * 10;
                    butterfly.position.set(
                        camera.position.x + Math.cos(angle) * radius,
                        butterfly.userData.originalY,
                        camera.position.z + Math.sin(angle) * radius
                    );
                }
            });
        }

        // Update spatial grid less frequently
        if (frameCount % SPATIAL_GRID_UPDATE_INTERVAL === 0) {
            updateSpatialGrid();
        }

        // Update day/night cycle
        updateDayNightCycle();
        
        // Only update visible objects
        if (frameCount % 2 === 0) {
            // Only update sun beams during day
            if (!isNightTime && sunBeams.some(beam => beam.visible)) {
                updateSunBeams();
            }
            
            // Only update visible clouds
            clouds.forEach(cloud => {
                if (cloud.position.distanceTo(camera.position) < VISIBLE_DISTANCE) {
                    updateCloud(cloud);
                }
            });
        }

        // Handle movement
        velocity.x -= velocity.x * 5.0 * delta;
        velocity.z -= velocity.z * 5.0 * delta;

        direction.z = Number(moveBackward) - Number(moveForward);
        direction.x = Number(moveRight) - Number(moveLeft);
        direction.normalize();

        const speedMultiplier = isSprinting && stamina > 0 ? SPRINT_MULTIPLIER : 1;

        // Calculate new position before moving
        const newPosition = camera.position.clone();
        const isMoving = moveForward || moveBackward || moveLeft || moveRight;
        
        if (isMoving) {
            if (moveForward || moveBackward) {
                velocity.z -= direction.z * MOVEMENT_SPEED * delta * speedMultiplier;
                newPosition.z -= velocity.z * delta;
            }
            if (moveLeft || moveRight) {
                velocity.x -= direction.x * MOVEMENT_SPEED * delta * speedMultiplier;
                newPosition.x -= velocity.x * delta;
            }

            // Update bob timer when moving
            bobTimer += delta * BOB_SPEED * speedMultiplier;
            // Apply camera bob
            camera.position.y = 1.7 + Math.sin(bobTimer) * BOB_AMOUNT;
        } else {
            // Smoothly return to default height when not moving
            camera.position.y += (1.7 - camera.position.y) * 0.15;
        }

        // Handle collisions and movement
        const finalPosition = handleCollision(newPosition, camera.position);
        if (finalPosition.x !== camera.position.x) {
            controls.moveRight(finalPosition.x - camera.position.x);
        }
        if (finalPosition.z !== camera.position.z) {
            controls.moveForward(camera.position.z - finalPosition.z);
        }

        // Update game stats
        if (isMoving) {
            distance += 0.1 * speedMultiplier;
            document.getElementById('distance').textContent = Math.floor(distance);
        }

        // Update game time
        gameTime += delta;
        if (frameCount % 30 === 0) { // Update UI less frequently
            const minutes = Math.floor(gameTime / 60);
            const seconds = Math.floor(gameTime % 60);
            document.getElementById('survival-time').textContent = 
                `Survival Time: ${minutes}m ${seconds}s`;
        }

        // Clean up and respawn objects less frequently
        if (frameCount % CLEANUP_INTERVAL === 0) {
            cleanupObjects();
        }
        
        // Update zombies less frequently
        if (isNightTime) {
            if (frameCount % ZOMBIE_UPDATE_INTERVAL === 0) {
                updateZombies();
                if (frameCount % 30 === 0) { // Spawn check less frequently
                    respawnZombies();
                }
            }
        }

        // Update other game elements
        if (frameCount % 2 === 0) {
            updateStamina();
            updateMinimap();
        }

        // Reset frame counter to prevent overflow
        if (frameCount > 1000) {
            frameCount = 0;
        }
    }

    renderer.render(scene, camera);
}

// Optimize single cloud update
function updateCloud(cloud) {
    cloud.position.x += 0.2; // Wind speed
    
    if (cloud.position.x > 250) {
        cloud.position.x = -250;
        cloud.position.z = (Math.random() - 0.5) * 200;
        cloud.position.y = 80 + Math.random() * 20;
    }

    // Simple floating motion
    cloud.position.y += Math.sin(gameTime * 0.5) * 0.02;
    cloud.rotation.y = Math.sin(gameTime * 0.2) * 0.02;
}

function createButterfly() {
    const butterfly = new THREE.Group();
    
    // Create body
    const bodyGeometry = new THREE.BoxGeometry(0.1, 0.3, 0.1);
    const body = new THREE.Mesh(bodyGeometry, butterflyBodyMaterial);
    butterfly.add(body);
    
    // Create wings
    const wingGeometry = new THREE.BoxGeometry(0.5, 0.4, 0.01);
    const leftWing = new THREE.Mesh(wingGeometry, butterflyWingMaterial);
    const rightWing = new THREE.Mesh(wingGeometry, butterflyWingMaterial);
    
    leftWing.position.x = -0.25;
    rightWing.position.x = 0.25;
    
    butterfly.add(leftWing);
    butterfly.add(rightWing);
    
    // Random position around player
    const angle = Math.random() * Math.PI * 2;
    const radius = 10 + Math.random() * 20;
    butterfly.position.set(
        Math.cos(angle) * radius,
        1 + Math.random() * 3,
        Math.sin(angle) * radius
    );
    
    // Add animation properties
    butterfly.userData = {
        wingAngle: Math.random() * Math.PI * 2,
        flapSpeed: 0.1 + Math.random() * 0.1,
        moveAngle: Math.random() * Math.PI * 2,
        moveSpeed: 0.05 + Math.random() * 0.05,
        originalY: butterfly.position.y
    };
    
    return butterfly;
}

function createFlower() {
    const flower = new THREE.Group();
    
    // Create center
    const centerGeometry = new THREE.BoxGeometry(0.2, 0.2, 0.2);
    const center = new THREE.Mesh(centerGeometry, flowerCenterMaterial);
    center.position.y = 0.3;
    flower.add(center);
    
    // Create petals
    const petalGeometry = new THREE.BoxGeometry(0.3, 0.1, 0.3);
    for (let i = 0; i < 4; i++) {
        const petal = new THREE.Mesh(petalGeometry, flowerPetalMaterial);
        petal.position.y = 0.3;
        petal.rotation.y = (i * Math.PI) / 2;
        petal.position.x = Math.cos(petal.rotation.y) * 0.3;
        petal.position.z = Math.sin(petal.rotation.y) * 0.3;
        flower.add(petal);
    }
    
    // Add stem
    const stemGeometry = new THREE.BoxGeometry(0.05, 0.6, 0.05);
    const stemMaterial = new THREE.MeshStandardMaterial({ color: 0x2d5a27 });
    const stem = new THREE.Mesh(stemGeometry, stemMaterial);
    stem.position.y = 0.3;
    flower.add(stem);
    
    return flower;
} 